import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import styled from 'styled-components';

import {
  Headline,
  IconButton,
  Layout,
  LayoutProps,
  Text,
  TextButton,
} from '@oneaudi/unified-web-components';
import {
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_500,
  SYS_SPACE_RELATIVE_LG,
  ThemeProvider,
} from '@oneaudi/unified-web-common';
import { SYS_SPACE_RELATIVE_MD, SYS_SPACE_RELATIVE_XL } from '@oneaudi/unified-web-alpha-common';

import { ACTIONS, SELECTORS } from '@oneaudi/stck-store';
import { CTAConfig } from '@oneaudi/vtp-configuration-service';

import {
  dynamicStylingHelper,
  getRelatedProduct,
  MYAUDI_WISHLIST_LOCAL_STORAGE_KEY,
  MyAudiWishlistLoginLayer,
  useAuthContext,
  useMyAudiWishlistContext,
  useServicesContext,
  setSelectedVehicle,
  VehicleBasic,
  useTranslations,
  ActionButtonTranslations,
  useButtons,
  GenericButtonRethink,
  trackClick,
} from '@oneaudi/vtp-shared';

import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';

import { getAppContext, type PlpConfiguration } from '../../hooks/tileHook';
import {
  trackAddToFav,
  trackDetailsPageNav,
  trackRemoveFromFav,
} from '../../tracking/tilesTracking';

import {
  ButtonContainerRow,
  ButtonContainerColumn,
  FavoritesContainer,
  FavoritesOverlay,
  GridButtonContainer,
} from './ActionButtons.styles';

import { useVehicleDetailsPageUrl } from '../../hooks/useVehicleDetailsPageUrl';

import { FeatureServiceDependencies } from '../../FeatureHubAppDefinition';

interface Props {
  vehicle: VehicleBasic;
}

const ActionButtons: React.FC<Props> = ({ vehicle }) => {
  const { getFeatureServices, getEnvironmentConfig } = useServicesContext();
  const {
    'audi-tracking-service': trackingService,
    'gfa:layer-manager': layerManager,
    'audi-content-service': audiContentService,
    'vtp-configuration-service': configurationService,
  } = getFeatureServices<FeatureServiceDependencies>();

  const detailsPageLink = useVehicleDetailsPageUrl(vehicle) || '';

  const {
    'nemo.ui.sc.result.details.button': goToDetailsLabel = 'Go to Details',
    'nemo.ui.sc.result.addfavorite': addFavoriteLabel = '',
    'stockcars.button.favourites.is-not-saved.arialabel': favoriteAreaLabelNotSaved = '',
    'stockcars.button.favourites.is-saved.arialabel': favoriteAreaLabelSaved = '',
    'nemo.ui.sc.result.removefavorite': removeFavoriteLabel = '',
    'nemo.ui.sc.favorites.layer.headline': favoritesLayerHeadline = '',
    'nemo.ui.sc.favorites.layer.copy': favoritesLayerCopy = '',
    'nemo.ui.sc.favorites.layer.button.yes': favoritesLayerYes = 'Yes',
    'nemo.ui.sc.favorites.layer.button.no': favoritesLayerNo = 'No',
    'nemo.ui.sc.favorites.layer.outer.aria.label': favoritesLayerOuterAriaLabel = '',
    'nemo.ui.sc.favorites.layer.inner.aria.label': favoritesLayerInnerAriaLabel = '',
  } = useTranslations<ActionButtonTranslations>();

  const { authService, isAuthenticated } = useAuthContext();

  const appContext = getAppContext();
  const { vtpConfiguration } = audiContentService?.getContent()?.fields as PlpConfiguration;
  const { enableMyAudiWishlist } = vtpConfiguration?.fields ?? {};

  const dispatch = useDispatch();
  const isFavorite = useSelector((state: {}) =>
    SELECTORS.VEHICLES.isFavoriteVehicle(state, vehicle.id)
  );
  const [showFavoritesLayer, setShowFavoritesLayer] = useState(false);

  const myAudiWishlistApi = useMyAudiWishlistContext();

  const isInMyAudiWishlist = enableMyAudiWishlist && myAudiWishlistApi.isInWishlist(vehicle.id);

  const isNewCar = vehicle.type.toLowerCase() === 'n';

  // TODO should we move this somewhere up the tree so it doesnt need to run in all the ActionButtons?
  // But its better to have it together with the rest of the wishlist logic.
  useEffect(() => {
    if (!enableMyAudiWishlist || !isAuthenticated) return;

    const myAudiVehicleIdToWishlist = localStorage.getItem(MYAUDI_WISHLIST_LOCAL_STORAGE_KEY);

    if (!myAudiVehicleIdToWishlist || myAudiVehicleIdToWishlist !== vehicle.id) return;

    localStorage.removeItem(MYAUDI_WISHLIST_LOCAL_STORAGE_KEY);

    if (isInMyAudiWishlist) return;

    myAudiWishlistApi.add(vehicle.id, isNewCar ? 'NEW' : 'USED');
  }, [enableMyAudiWishlist, isAuthenticated, isInMyAudiWishlist, vehicle.id]);

  const relatedProduct = getRelatedProduct(vehicle);

  const [layerNode, setLayerNode] = useState<HTMLDivElement | null>(null);

  const addFavorites = () => {
    if (enableMyAudiWishlist) {
      myAudiWishlistApi.add(vehicle.id, isNewCar ? 'NEW' : 'USED');
    } else {
      dispatch(
        ACTIONS.FAVORITE_VEHICLES.addFavoriteVehicleId({
          vehicleId: vehicle.id,
        })
      );
    }

    if (trackingService) trackAddToFav(trackingService, addFavoriteLabel, relatedProduct);
  };

  const removeFavorites = () => {
    if (enableMyAudiWishlist) {
      myAudiWishlistApi.remove(vehicle.id);
    } else {
      dispatch(
        ACTIONS.FAVORITE_VEHICLES.removeFavoriteVehicleId({
          vehicleId: vehicle.id,
        })
      );
    }

    setShowFavoritesLayer(false);
    if (trackingService) trackRemoveFromFav(trackingService, removeFavoriteLabel, relatedProduct);
  };

  const handleFavoritesClick = () => {
    if (enableMyAudiWishlist) {
      if (!isAuthenticated) {
        layerManager?.openFocusLayer(
          () => <div ref={setLayerNode} />,
          {
            withFefa: true,
            primaryAriaLabel: favoritesLayerOuterAriaLabel,
            secondaryAriaLabel: favoritesLayerInnerAriaLabel,
          },
          {
            size: FocusLayerSizeV2.A,
            userCloseable: true,
          }
        );

        return;
      }

      if (isInMyAudiWishlist) {
        if (appContext !== 'results') {
          setShowFavoritesLayer(true);
        } else {
          removeFavorites();
        }
      } else {
        addFavorites();
      }

      return;
    }

    if (isFavorite) {
      if (appContext !== 'results') {
        setShowFavoritesLayer(true);
      } else {
        removeFavorites();
      }
    } else {
      addFavorites();
    }
  };

  const envConfig = getEnvironmentConfig();

  const { buttonsState, scContextId, phoneWithNumber } = useButtons(
    vehicle,
    'tiles',
    undefined,
    false,
    configurationService,
    envConfig,
    false,
    true
  );
  const trackButtonClick = (
    buttoninfo: CTAConfig,
    variant: string,
    trackingCode?: string,
    btnName?: string
  ) => {
    if (trackingService) {
      trackClick(
        trackingService,
        buttoninfo,
        variant,
        'vtp-results',
        vehicle.id,
        trackingCode,
        btnName
      );
    }
  };
  const hasCTAInConfig = !!buttonsState?.length;
  const fallbackDetailsCta = {
    type: 'details',
    label: goToDetailsLabel,
  } as CTAConfig;
  const maxTwoCTAs = hasCTAInConfig ? buttonsState.slice(0, 2) : [fallbackDetailsCta];
  const [cta1, cta2] = maxTwoCTAs.map((button, index) => {
    const variant = index === 0 ? 'primary' : 'secondary';
    return button.type === 'details' ? (
      <TextButton
        size={dynamicStylingHelper(undefined, 'sm')}
        variant={variant}
        data-testid="detailsBtn"
        href={detailsPageLink}
        isFullWidth
        onClick={() => {
          setSelectedVehicle(vehicle.id);
          if (trackingService) {
            trackDetailsPageNav(
              trackingService,
              `${button.label}`,
              detailsPageLink,
              'button',
              `${variant} button`
            );
          }
        }}
      >
        {button.label}
      </TextButton>
    ) : (
      <GenericButtonRethink
        key={`Button-vehicle-${vehicle.id}`}
        vehicle={vehicle}
        buttonInfo={button}
        appContext="tiles"
        variant={variant}
        data-testId={`standard-button-${variant}`}
        layerManager={layerManager}
        scContextId={scContextId}
        phoneWithNumber={phoneWithNumber}
        configurationService={configurationService}
        trackButtonClick={trackButtonClick}
        showAsText={false}
        isMobileScreenSize
      />
    );
  });
  return (
    <>
      <ButtonContainerColumn>
        {cta2 && cta1}
        <ButtonContainerRow>
          {!cta2 && cta1}
          {cta2}
          <FavoritesContainer>
            <IconButton
              variant="secondary"
              icon={isFavorite || isInMyAudiWishlist ? 'favorite-active' : 'favorite'}
              size={dynamicStylingHelper('md', 'sm')}
              type="button"
              onClick={handleFavoritesClick}
              isLoading={myAudiWishlistApi.isLoading}
              data-testid="favoritesBtn"
              aria-label={
                isFavorite || isInMyAudiWishlist
                  ? favoriteAreaLabelSaved
                  : favoriteAreaLabelNotSaved
              }
            />
          </FavoritesContainer>
        </ButtonContainerRow>
      </ButtonContainerColumn>
      {showFavoritesLayer && (
        <ThemeProvider theme={{ name: 'dark' }}>
          <FavoritesOverlay>
            <StyledLayout
              direction="column"
              justify="center"
              margin={[0, 'var(--spacing-relative-xl)']}
            >
              <Headline
                variant={{
                  order: dynamicStylingHelper('2', '3'),
                  weight: 'normal',
                  type: 'headline',
                }}
                tag="h2"
                margin={[0, 0, dynamicStylingHelper(SYS_SPACE_FIXED_150, SYS_SPACE_RELATIVE_MD), 0]}
              >
                {favoritesLayerHeadline}
              </Headline>
              <Text
                variant={{ order: '2', style: 'normal', type: 'copy' }}
                margin={[0, 0, dynamicStylingHelper(SYS_SPACE_FIXED_500, SYS_SPACE_RELATIVE_XL), 0]}
              >
                {favoritesLayerCopy}
              </Text>
              <GridButtonContainer data-testid="removeFavoriteLayer">
                <TextButton
                  variant="primary"
                  onClick={removeFavorites}
                  isLoading={myAudiWishlistApi.isLoading}
                  data-testid="removeFavorite"
                  size={dynamicStylingHelper('md', 'sm')}
                >
                  {favoritesLayerYes}
                </TextButton>
                <TextButton
                  variant="secondary"
                  onClick={() => setShowFavoritesLayer(false)}
                  data-testid="closeRemoveFavoriteLayer"
                  size={dynamicStylingHelper('md', 'sm')}
                >
                  {favoritesLayerNo}
                </TextButton>
              </GridButtonContainer>
            </StyledLayout>
          </FavoritesOverlay>
        </ThemeProvider>
      )}

      {layerNode &&
        ReactDOM.createPortal(
          <MyAudiWishlistLoginLayer authService={authService} vehicle={vehicle} />,
          layerNode
        )}
    </>
  );
};

export default ActionButtons;

export const StyledLayout = styled((props) => <Layout {...props} />)<LayoutProps>`
  width: ${dynamicStylingHelper('unset', `calc(100% - 2*${SYS_SPACE_RELATIVE_LG})`)};
`;
